import { AuthenticateResponse, JoinChannels, LeaveChannels } from '@/api/dtos';
import {
    ISignalRContext,
    ISignalRListeners,
    ISignalROnClose,
    ISignalROnConnect,
    ISignalROnJoin,
    ISignalROnLeave,
    ISignalROnMessage,
    ISignalROnReconnect,
    ISignalROnReconnecting,
    ISignalRUnsubscribe,
} from '@/contexts/api/SignalRContext';
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr/dist/esm';
import { JsonServiceClient } from '@servicestack/client';

type ChannelEventHandler = (...args: any[]) => any;

interface ChannelEventHandlerRegistration {
    connection: HubConnection;
    handler: ChannelEventHandler;
}

/**
 * Owns one SignalR connection and its channel subscriptions independently of React.
 *
 * SignalR retains every callback passed to connection.on(). The old reconnect path
 * created another callback whenever it rejoined a channel, so each reconnect caused
 * one additional delivery of every later message. This manager stores the exact
 * callback registered for each channel and connection. Rejoining a server-side group
 * therefore reuses its local callback, while replacing or disposing a connection
 * removes that same callback with connection.off().
 */
export class SignalRManager implements ISignalRContext {
    private listeners: Record<string, ISignalRListeners> = {};
    private connection?: HubConnection;
    private client?: JsonServiceClient;
    private readonly baseUrl: string;
    private readonly debug: boolean;
    private activeChannels = new Set<string>();
    private channelEventHandlers = new Map<string, ChannelEventHandlerRegistration>();
    private closingConnections = new Set<HubConnection>();
    private user?: AuthenticateResponse;
    private connectionVersion = 0;
    private subscriptionDelay = 0;
    private subscriptionTimer: number | undefined;

    constructor(
        baseUrl: string,
        debug: boolean,
        client?: JsonServiceClient,
        user?: AuthenticateResponse,
    ) {
        this.baseUrl = baseUrl;
        this.debug = debug;
        this.client = client;
        this.user = user;
    }

    public setUser = (user?: AuthenticateResponse) => {
        if (this.debug) {
            console.log('SignalRManager - setUser:', user);
        }

        if (this.user === user) {
            return;
        }

        this.user = user;

        if (this.subscriptionTimer) {
            window.clearTimeout(this.subscriptionTimer);
        }

        this.scheduleSubscription();
    };

    public setClient = (client?: JsonServiceClient) => {
        this.client = client;
    };

    private getListeners = <TListener = Function>(
        type: keyof ISignalRListeners,
        channel?: string,
    ): TListener[] => {
        if (channel) {
            return (this.listeners[channel]?.[type] as unknown as TListener[] | undefined) ?? [];
        }

        return Object.keys(this.listeners)
            .flatMap(key => this.listeners[key][type] as unknown as TListener[]);
    };

    private getChannels = () => Object.keys(this.listeners);

    private isCurrent = (connection: HubConnection, version: number) =>
        this.connection === connection && this.connectionVersion === version;

    /**
     * Installs one stable message callback for this channel on this connection.
     * Reconnects call this method again, but the registration map prevents another
     * connection.on() call for a callback which is already installed.
     */
    private addChannelEventHandler = (connection: HubConnection, channel: string) => {
        const existing = this.channelEventHandlers.get(channel);

        if (existing?.connection === connection)
        {
            return;
        }

        if (existing) {
            existing.connection.off(channel, existing.handler);
        }

        const handler = async (data: any) => {
            if (this.debug)
            {
                console.log(`SignalRManager - onMessage - ${channel}`, data);
            }

            for (const listener of this.getListeners<ISignalROnMessage>('onMessage', channel)) {
                await listener(connection, data);
            }
        };

        connection.on(channel, handler);
        this.channelEventHandlers.set(channel, { connection, handler });
    };

    /** Removes the exact callback previously supplied to connection.on(). */
    private removeChannelEventHandler = (channel: string, connection?: HubConnection) => {
        const registration = this.channelEventHandlers.get(channel);

        if (!registration || (connection && registration.connection !== connection)) {
            return;
        }

        registration.connection.off(channel, registration.handler);
        this.channelEventHandlers.delete(channel);
    };

    private joinChannels = async (channels?: string[]) => {
        if (!channels?.length) {
            return;
        }

        const connection = this.connection;
        const version = this.connectionVersion;

        if (!connection?.connectionId) {
            return;
        }

        // Install the local callback before joining the server group so a message sent
        // immediately after the join response cannot be missed. This remains safe on
        // reconnect because addChannelEventHandler reuses an existing registration.
        for (const channel of channels) {
            this.addChannelEventHandler(connection, channel);
        }

        await this.client?.post(new JoinChannels({
            channels,
            connectionId: connection.connectionId,
        }));

        if (!this.isCurrent(connection, version)) {
            return;
        }

        for (const channel of channels) {
            if (!this.isCurrent(connection, version)) {
                return;
            }

            this.activeChannels.add(channel);
            await this.onChannelJoined(channel, connection, version);
        }
    };

    private leaveChannels = async (channels?: string[]) => {
        if (!channels?.length) {
            return;
        }

        const connection = this.connection;
        const version = this.connectionVersion;

        if (!connection?.connectionId) {
            return;
        }

        await this.client?.post(new LeaveChannels({
            channels,
            connectionId: connection.connectionId,
        }));

        if (!this.isCurrent(connection, version)) {
            return;
        }

        for (const channel of channels) {
            if (!this.isCurrent(connection, version)) {
                return;
            }

            this.removeChannelEventHandler(channel, connection);
            this.activeChannels.delete(channel);
            await this.onChannelLeave(channel, connection, version);
        }
    };

    private onChannelJoined = async (channel: string, connection: HubConnection, version: number) => {
        if (this.debug) {
            console.log('SignalRManager - onJoin', channel);
        }

        if (!this.isCurrent(connection, version)) {
            return;
        }

        for (const listener of this.getListeners<ISignalROnJoin>('onJoin', channel)) {
            await listener(connection);
        }
    };

    private onChannelLeave = async (channel: string, connection: HubConnection, version: number) => {
        if (this.debug) {
            console.log('SignalRManager - onLeave', channel);
        }

        if (!this.isCurrent(connection, version)) {
            return;
        }

        for (const listener of this.getListeners<ISignalROnLeave>('onLeave', channel)) {
            await listener(connection);
        }
    };

    private onConnect = async (connection: HubConnection, version: number) => {
        if (this.debug) {
            console.log('SignalRManager - onConnect');
        }

        if (!this.isCurrent(connection, version)) {
            return;
        }

        for (const listener of this.getListeners<ISignalROnConnect>('onConnect')) {
            await listener(connection);
        }
    };

    private onReconnected = async (connection: HubConnection, connectionId?: string) => {
        const version = this.connectionVersion;

        if (!this.isCurrent(connection, version)) {
            return;
        }

        if (this.debug) {
            console.log('SignalRManager - onReconnect', connectionId);
        }

        // SignalR reconnects with a new connection ID and loses server-side group
        // membership. Rejoin each group, while retaining one local callback per channel.
        if (connectionId) {
            await this.joinChannels(this.getChannels());
        }

        if (!this.isCurrent(connection, version)) {
            return;
        }

        for (const listener of this.getListeners<ISignalROnReconnect>('onReconnect')) {
            await listener(connection, connectionId);
        }
    };

    private onReconnecting = async (connection: HubConnection, error?: Error) => {
        if (this.debug) {
            console.log('SignalRManager - onReconnecting', error);
        }

        if (!this.isCurrent(connection, this.connectionVersion)) {
            return;
        }

        // The HubConnection object is reused with a new connection ID. Advancing the
        // version prevents an earlier join or leave response from changing current state.
        ++this.connectionVersion;

        for (const listener of this.getListeners<ISignalROnReconnecting>('onReconnecting')) {
            await listener(connection, error);
        }
    };

    private onClose = async (connection: HubConnection, error?: Error) => {
        if (this.debug) {
            console.log('SignalRManager - onClose', error);
        }

        if (!this.isCurrent(connection, this.connectionVersion)
            && !this.closingConnections.has(connection)) {
            return;
        }

        for (const listener of this.getListeners<ISignalROnClose>('onClose')) {
            await listener(connection, error);
        }
    };

    private createConnection = async () => {
        const connection = new HubConnectionBuilder()
            .withUrl(`${this.baseUrl}signal-r/sdv`, { withCredentials: false })
            .withKeepAliveInterval(10000)
            .withAutomaticReconnect()
            .build();

        const version = ++this.connectionVersion;
        this.connection = connection;

        connection.onreconnected(id => this.onReconnected(connection, id));
        connection.onreconnecting(error => this.onReconnecting(connection, error));
        connection.onclose(error => this.onClose(connection, error));

        if (this.debug) {
            console.log('SignalRManager - Created Client', connection);
        }

        try {
            await connection.start();
        }
        catch (error) {
            if (this.isCurrent(connection, version)) {
                await this.disposeConnection(connection, version);
            }

            throw error;
        }

        if (!this.isCurrent(connection, version)) {
            await connection.stop();
            return;
        }

        await this.onConnect(connection, version);

        if (!this.isCurrent(connection, version)) {
            return;
        }

        // Keep the one stable handler after a failed join so a later retry can reuse it.
        await this.joinChannels(this.getChannels());
    };

    private disposeConnection = async (
        connection = this.connection,
        version = this.connectionVersion,
    ) => {
        if (!connection || !this.isCurrent(connection, version)) {
            return;
        }

        ++this.connectionVersion;

        if (this.debug) {
            console.log('SignalRManager - Disposed of Connection', connection);
        }

        for (const [channel, registration] of this.channelEventHandlers) {
            if (registration.connection === connection) {
                this.removeChannelEventHandler(channel, connection);
            }
        }

        this.connection = undefined;
        this.activeChannels.clear();
        this.closingConnections.add(connection);

        try {
            await connection.stop();
        }
        finally {
            this.closingConnections.delete(connection);
        }
    };

    private updateSubscription = async () => {
        if (!this.user) {
            await this.disposeConnection();
            return;
        }

        const channels = this.getChannels();

        if (!this.connection) {
            if (channels.length) {
                await this.createConnection();
            }

            return;
        }

        if (!channels.length) {
            await this.disposeConnection();
            return;
        }

        // Local callback registration and server-side group membership are separate.
        // Reconcile callbacks even when the group is already active. This restores a
        // callback after rapid unsubscribe/resubscribe and prevents failed-join leaks.
        for (const channel of channels) {
            this.addChannelEventHandler(this.connection, channel);
        }

        const active = Array.from(this.activeChannels);
        const newChannels = channels.filter(channel => !active.includes(channel));
        const oldChannels = active.filter(channel => !channels.includes(channel));

        if (newChannels.length) {
            await this.joinChannels(newChannels);
        }

        if (oldChannels.length) {
            await this.leaveChannels(oldChannels);
        }
    };

    private scheduleSubscription = () => {
        if (this.subscriptionTimer) {
            window.clearTimeout(this.subscriptionTimer);
        }

        this.subscriptionTimer = window.setTimeout(
            () => {
                void this.updateSubscription().catch(error =>
                    console.error('SignalRManager - subscription update failed', error));
            },
            this.subscriptionDelay,
        );
    };

    private ensureChannel = (channel: string) => {
        if (this.listeners[channel]) {
            return;
        }

        this.listeners[channel] = {
            onConnect: [],
            onJoin: [],
            onLeave: [],
            onMessage: [],
            onReconnect: [],
            onClose: [],
            onReconnecting: [],
        };

        this.scheduleSubscription();
    };

    private cleanChannel = (channel: string) => {
        const listeners = this.listeners[channel];

        if (!listeners) {
            return;
        }

        if (Object.values(listeners).every(value => value.length === 0)) {
            // A failed join never reaches activeChannels, so remove its local callback
            // immediately rather than waiting for leaveChannels to remove it.
            this.removeChannelEventHandler(channel);
            delete this.listeners[channel];
            this.scheduleSubscription();
        }
    };

    private addListener = <T extends keyof ISignalRListeners>(
        channel: string,
        type: T,
        listener: ISignalRListeners[T][number],
    ): ISignalRUnsubscribe => {
        this.ensureChannel(channel);
        (this.listeners[channel][type] as unknown as unknown[]).push(listener);

        return () => {
            if (this.listeners[channel]) {
                this.listeners[channel][type] = (
                    this.listeners[channel][type] as unknown[]
                ).filter(item => item !== listener) as never;
            }

            this.cleanChannel(channel);
        };
    };

    public addOnConnectListener = (channel: string, listener: ISignalROnConnect) =>
        this.addListener(channel, 'onConnect', listener);

    public addOnJoinListener = (channel: string, listener: ISignalROnJoin) =>
        this.addListener(channel, 'onJoin', listener);

    public addOnLeaveListener = (channel: string, listener: ISignalROnLeave) =>
        this.addListener(channel, 'onLeave', listener);

    public addOnMessageListener = <TData = any>(
        channel: string,
        listener: ISignalROnMessage<TData>,
    ) => this.addListener(channel, 'onMessage', listener);

    public addOnReconnectListener = (channel: string, listener: ISignalROnReconnect) =>
        this.addListener(channel, 'onReconnect', listener);

    public addOnCloseListener = (channel: string, listener: ISignalROnClose) =>
        this.addListener(channel, 'onClose', listener);

    public addOnReconnectingListener = (
        channel: string,
        listener: ISignalROnReconnecting,
    ) => this.addListener(channel, 'onReconnecting', listener);

    public dispose = async () => {
        if (this.subscriptionTimer) {
            window.clearTimeout(this.subscriptionTimer);
        }

        this.subscriptionTimer = undefined;
        await this.disposeConnection();
    };
}

export default SignalRManager;
